/**
 * The App Studio overlay (`appstudio.overlay/v1`), as `indskr_GetScreenBundle` returns it.
 *
 * Only what go-app reads is typed. Node `type` and every feature-specific property stay open
 * on purpose: an overlay may carry nodes and props this app has no component for, and an
 * unknown node must render as a visible placeholder with a warning, never crash the screen.
 *
 * See docs/features/order-capture.md.
 */

/** An overlay expression, evaluated by `app-ui-expression.ts`. */
export type OverlayExpression = string;

export type OverlayNodeType =
  | 'stack'
  | 'tabs'
  | 'tabPage'
  | 'section'
  | 'field'
  | 'matrixGrid'
  | 'summaryGrid'
  | 'summaryPanel'
  | 'historyPanel'
  | (string & NonNullable<unknown>);

/** Semantic tone, shared by badges and pills. */
export type OverlayTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

/** A count badge on a tab label, from a loaded dataset or a FetchXML count. */
export interface OverlayBadge {
  dataset?: string;
  fetchXml?: string;
  tone?: OverlayTone;
  hideWhenZero?: boolean;
}

/** One key/value item of a `summaryPanel`. */
export interface OverlaySummaryItem {
  id: string;
  label: string;
  computed: OverlayExpression;
  format?: 'number' | 'currency' | 'text';
  emphasis?: boolean;
}

/** `rowSource`: where a node's rows come from, and how a row is named. */
export interface OverlayRowSource {
  /** The dataset a row is one row of, e.g. the price list's items. */
  dataset?: string;
  /** `parentAttr/entity.attribute`, e.g. `customerid/salesorder.customerid`. */
  path?: string;
  /** An attribute, `-` first for descending. */
  orderBy?: string;
  top?: number;
  /** matrixGrid: up to four lines of row text, the first the row's name. */
  labels?: OverlayRowLabel[];
  /** summaryGrid, retired by the contract: what names a line. */
  labelAttr?: string;
  subLabelAttr?: string;
}

/** One `rowSource.labels` entry: an attribute, on the row (`source` absent) or an expanded record. */
export interface OverlayRowLabel {
  attribute: string;
  source?: string;
}

/** `schema`: the records a node's rows are, by role (`history`, `line`, `delivery`). */
export interface OverlaySchemaEntry {
  /** Further `…Attr` columns the node reads, e.g. `nameAttr`. */
  [attr: string]: string | undefined;
  entity?: string;
  idAttr?: string;
  dateAttr?: string;
  deliveryAttr?: string;
  descriptionAttr?: string;
  productNumberAttr?: string;
}

export type OverlaySchema = Record<string, OverlaySchemaEntry | undefined>;

/** `card`: a list row's layout, attribute names and a footer template of `{{attribute}}` tokens. */
export interface OverlayCard {
  title?: string;
  badge?: string;
  meta?: string[];
  value?: string;
  footer?: string;
  /** v1.24: up to four columns between the title and the footer, in place of `meta`. */
  fields?: string[];
}

/** One of a grid's `gridColumns`. */
export interface OverlayGridColumn {
  id?: string;
  attribute?: string;
  field?: string;
  label?: string;
  /** Evaluated per row instead of reading a column. Never editable. */
  computed?: string;
  readOnly?: boolean;
  widthPct?: number;
  minWidthPct?: number;
  maxWidthPct?: number;
}

export interface OverlaySearch {
  placeholder?: string;
  /** What the search box matches, as the feature names them. */
  attributes?: string[];
  /** A chip per category band, to narrow the list. */
  categoryChips?: boolean;
}

/** `columnSource.cadence`: the schedule picker, and what a choice writes on the record. */
export interface OverlayCadence {
  /** The record's lookup to the schedule. */
  path?: string;
  /** `confirm`: with quantities typed, the rep chooses to keep or clear them. */
  onChange?: string;
  /** Record column ← schedule field, copied onto the record. */
  snapshot?: Record<string, string>;
}

/** `columnSource` of a matrixGrid: how its columns are made. */
export interface OverlayColumnSource {
  /** The dataset a column is one row of, e.g. the order's deliveries. */
  dataset?: string;
  /** An attribute, `-` first for descending. */
  orderBy?: string;
  addable?: boolean;
  removable?: boolean;
  maxColumns?: number;
  cadence?: OverlayCadence;
  /** Set: each column ships to one of the account's addresses. */
  addressSource?: unknown;
}

/** `lock`: when a grid is read-only, and what it says then. */
export interface OverlayLock {
  when?: OverlayExpression;
  message?: string;
}

/** `footer` of the node carrying the totals: its figures by id, in order. */
export interface OverlayFooter {
  items?: string[];
  labels?: Record<string, string>;
  /** Figures shown between the pair and the headline, e.g. an order discount. */
  between?: string[];
}

/** One of a summaryGrid's `lines.columns`. */
export interface OverlaySummaryColumn {
  id: string;
  label: string;
  /** A published column's expression; an `extraColumns` entry names an `attribute` instead. */
  computed?: string;
  attribute?: string;
  format?: string;
}

/** `lines.extraColumns` (contract v1.15): a line attribute shown as a column of its own. */
export interface OverlaySummaryExtraColumn {
  attribute?: string;
  label?: string;
  /** text, number, currency, percent or date. */
  format?: string;
}

/** summaryGrid `lines`: one row per line of the group. */
export interface OverlaySummaryLines {
  dataset?: string;
  columns?: OverlaySummaryColumn[];
  extraColumns?: OverlaySummaryExtraColumn[];
  /** Contract v1.22: `unit` | `qty` | `disc` | `line` | `extra:<attribute>` (`extra:<attribute>:2` for a repeat). */
  columnOrder?: string[];
  hideZero?: boolean;
  totalTemplate?: string;
}

/** summaryGrid `groupSource`: one card per group. */
export interface OverlaySummaryGroups {
  dataset?: string;
  orderBy?: string;
  titleTemplate?: string;
  subtitleTemplate?: string;
  hideEmpty?: boolean;
}

export interface OverlayTimeline {
  activities?: { entity: string; statusReasons?: number[] }[];
  scopes?: string[];
  defaultScope?: string;
  /** Not yet seen in a published screen; read as "show notes too" when true. */
  notes?: boolean;
}

export interface OverlayNode {
  /** Feature-specific props (`rowSource`, `cell`, `card`, …) read by the node's own component. */
  [prop: string]: unknown;
  type: OverlayNodeType;
  id?: string;
  title?: string;
  /** Registry key: `type#variant`. A component reference object is also allowed. */
  variant?: string | { component: string; props?: Record<string, unknown> };
  /**
   * Column count for a container that lays children side by side. 1–4: Dynamics caps a
   * section at four, and the designer, the published formxml and this app all clamp there
   * so one authored number renders the same everywhere. Absent means one column.
   */
  columns?: number;
  /** Column span within a multi-column parent, clamped to the parent's `columns`. */
  span?: number;
  children?: OverlayNode[];

  visibleWhen?: OverlayExpression;
  requiredWhen?: OverlayExpression;
  disabledWhen?: OverlayExpression;

  /** Shown by list-like nodes when they have nothing to show. Tenant text. */
  emptyText?: string;
  /** v1.24, `section`: false hides the heading; the title stays for Dynamics, which needs one. */
  showLabel?: boolean;

  // ---- tabPage ----
  badge?: OverlayBadge;

  // ---- field ----
  /** Join key into FormMetadataV2 `Control.AttributeName`. */
  attribute?: string;
  required?: boolean;
  readOnly?: boolean;
  readOnlyWhen?: OverlayExpression;
  /** Helper text shown with the field. */
  hint?: string;
  /** Set by go-app, never configured: a read-only strip slot draws as its label over its value. */
  presentation?: 'readout';
  /**
   * Options read from a synced dataset — the offline-safe lookup. `entity` is the table a
   * lookup's ids belong to; it travels with the chosen value so the write can bind to it.
   */
  optionsFrom?: {
    dataset: string;
    valueAttr: string;
    labelAttr: string;
    entity?: string;
  };
  /** Options narrowed by another field's value: `map[thatValue] = allowed option values`. */
  optionsFilter?: {
    dependsOn: string;
    map: Record<string, Array<number | string>>;
  };
  /** Boolean fields: the sibling node id shown only while this field is true. */
  reveals?: string;
  /** v1.6: `{ value }`, `{ from: path }` or, for a lookup, `{ id, name, entity }`. */
  defaultValue?: OverlayFieldDefault;
  autoFill?: OverlayAutoFill;
  lookupView?: OverlayLookupView;
  /** v1.7: the On Change logic to run when this field changes. */
  onChange?: { handler?: string };

  // ---- named component (V3 mobile app form) ----
  /**
   * The code component behind this node, as the form names it — e.g.
   * `indskr_Omnipresence.OrderDeliveryMatrix`. Resolved before `type#variant`.
   */
  customControlName?: string;

  // ---- summaryPanel ----
  items?: OverlaySummaryItem[];

  // ---- lists and grids ----
  rowSource?: OverlayRowSource;
  schema?: OverlaySchema;
  card?: OverlayCard;
  /** What a list offers on each row, e.g. Reorder. */
  actions?: OverlayRowAction[];
  /** A card's badge style by raw value. */
  badgeStyles?: Record<string, { label: string; tone: OverlayTone }>;
  /** A view the list narrows instead of its own FetchXML. */
  view?: { id?: string };
  /** v1.24, `historyPanel`: the id of the screen's `dataSources` entry it lists. */
  dataSource?: string;
  /** v1.24, `historyPanel`: the title reads "Title (n)". */
  showCount?: boolean;
  gridColumns?: OverlayGridColumn[];
  search?: OverlaySearch;
  /** Words the node lets the configuration override, by name. */
  labels?: Record<string, string>;

  // ---- matrixGrid ----
  columnSource?: OverlayColumnSource;
  lock?: OverlayLock;
  footer?: OverlayFooter;

  // ---- summaryGrid ----
  lines?: OverlaySummaryLines;
  groupSource?: OverlaySummaryGroups;

  // ---- timeline ----
  timeline?: OverlayTimeline;
}

/** The closed set of actions a config may name. Unknown ones never run. */
export type OverlayBuiltInAction =
  | 'saveDraft'
  | 'validateAndSubmit'
  | 'addRow'
  | 'removeRow'
  | 'openModal'
  | 'sync'
  | 'navigate'
  | 'createChild'
  | 'cloneRecord'
  | (string & NonNullable<unknown>);

/** An action a list node offers on each of its rows, e.g. Reorder on a past order. */
export interface OverlayRowAction {
  action: OverlayBuiltInAction;
  label: string;
  confirm?: boolean;
  confirmMessage?: string;
}

export interface OverlayActionRun {
  /** A built-in action, or else `handler`: a command in the bundle's `logic` (v1.7). */
  action?: OverlayBuiltInAction;
  handler?: string;
  /** Save / Submit: `back` leaves the screen once the record is saved; the default stays. */
  afterSave?: 'back' | 'stay';
  /** v1.13: the id of a confirm popup in `screen.modals` asked before a built-in action runs. */
  confirmModal?: string;
  params?: Record<string, unknown>;
  showValidationSummary?: boolean;
}

/** A screen-level button, as `screen.actions[]` declares it. */
export interface OverlayAction {
  id: string;
  label: string;
  kind?: 'primary' | 'secondary' | 'danger';
  run: OverlayActionRun;
  visibleWhen?: OverlayExpression;
  disabledWhen?: OverlayExpression;
  confirm?: boolean;
  confirmMessage?: string;
  /** Set by go-app, not by config. An icon-only button keeps `label` as its name. */
  tone?: 'tinted';
  icon?: string;
}

export interface OverlayFieldDefault {
  value?: unknown;
  from?: string;
  id?: string;
  name?: string;
  entity?: string;
}

/**
 * v1.5: a lookup's candidates. Publish copies the view's `fetchXml` and turns each bound condition
 * value into `{{from}}`; `columns` are what each candidate shows.
 */
export interface OverlayLookupView {
  id?: string;
  fetchXml?: string;
  bindings?: { at?: string; operator?: string; from: string }[];
  columns?: OverlayLookupColumn[];
  /** Dot paths through N:1 lookups added to the view's own columns, used when `columns` is absent. */
  extraColumns?: string[];
  searchable?: string[];
  /** A bound field that is empty: `ignore` drops its condition, `none` offers nothing. */
  whenEmpty?: 'ignore' | 'none';
}

export interface OverlayLookupColumn {
  path: string;
  /** The key the column has in a result row. */
  field?: string;
  label?: string;
  type?: string;
  searchable?: boolean;
  /** Fetched only so search can match it; not shown. */
  hidden?: boolean;
}

/** v1.6: `copy` stays editable, `calculate` is read-only; both rerun when an `inputs` field changes. */
export interface OverlayAutoFill {
  copy?: string;
  calculate?: string;
  inputs?: string[];
}

/**
 * One of a strip's four slots (contract v1.8): a field of the record on screen, rendered as the
 * same field in the body, or a button. An empty slot is `null`.
 */
export interface OverlaySlot {
  field?: string;
  readOnly?: boolean;
  appType?: string;
  decimals?: number;
  button?: OverlayAction;
}

export interface OverlaySubmitGuard {
  when: OverlayExpression;
  message: string;
}

/** The `screen` block of a published overlay: the layout root and what surrounds it. */
export interface OverlayScreen {
  [prop: string]: unknown;
  title?: string;
  layout: OverlayNode;
  /** Before v1.8; a published screen now puts its buttons in `header.slots` / `footer.slots`. */
  actions?: OverlayAction[];
  header?: {
    [prop: string]: unknown;
    slots?: (OverlaySlot | null)[];
    /** The title, bound to the record. */
    titleBinding?: OverlayExpression;
    /** The button the back arrow runs instead of closing. */
    cancelAction?: string;
    /** The record's status, styled by raw value. */
    badge?: {
      bind?: string;
      styles?: Record<string, { label: string; tone: string }>;
    };
  };
  footer?: { [prop: string]: unknown; slots?: (OverlaySlot | null)[] };
  submit?: { [prop: string]: unknown; guards?: OverlaySubmitGuard[] };
  /** Dataset declarations, keyed by name. Only the keys matter to the renderer. */
  datasets?: Record<string, unknown>;
  /** v1.24: queries a history panel lists (`historyPanel.dataSource`). Not `datasets`, a retired key. */
  dataSources?: OverlayDataSource[];
  /** Before v1.7: logic bodies inline, by name. */
  handlers?: Record<string, unknown>;
  /** Popups by id, as a list or a map; buttons reach the confirm ones through `run.confirmModal`. */
  modals?: OverlayModal[] | Record<string, OverlayModal>;
}

export interface OverlayModal {
  [prop: string]: unknown;
  id?: string;
  type?: string;
  title?: string;
  /** May show values: `{{attribute}}`. */
  message?: string;
  confirmLabel?: string;
}

/** A v1.24 `screen.dataSources` entry: FetchXML whose `{{name}}` tokens read the record on screen. */
export interface OverlayDataSource {
  id: string;
  kind?: 'fetchXml';
  label?: string;
  entity?: string;
  fetchXml?: string;
  /** `{{name}}` is filled from the record's `column`; a token with no entry reads its own name. */
  placeholders?: { name: string; column: string }[];
  /** Replaces the query's own order when set. */
  orderBy?: { attribute: string; descending?: boolean }[];
}

export const OVERLAY_MAX_COLUMNS = 4;

/** `indskr_mobileappform.indskr_metadataversion` values. V3 carries App Studio overlay props per cell. */
/* eslint-disable @typescript-eslint/naming-convention -- named after the Dynamics option labels */
export const MOBILE_FORM_METADATA_VERSION = {
  V1: 548910000,
  V2: 548910001,
  V3: 548910002,
} as const;
/* eslint-enable @typescript-eslint/naming-convention */

/** Clamps an authored column count to what every consumer agrees on. */
export function clampColumns(columns: number | undefined): number {
  if (typeof columns !== 'number' || !Number.isFinite(columns)) return 1;
  return Math.min(OVERLAY_MAX_COLUMNS, Math.max(1, Math.trunc(columns)));
}

/**
 * The screen block from whatever the bundle put in `layout`.
 *
 * The action stores the overlay verbatim, but "verbatim" has meant three shapes across the
 * contract's versions: the layout root node alone, the `screen` block, or the whole document
 * with `screen` inside. Accepting all three costs nothing and keeps a contract change on
 * the server from blanking every screen on the device.
 */
export function resolveOverlayScreen(layout: unknown): OverlayScreen | null {
  if (!layout || typeof layout !== 'object') return null;
  const candidate = layout as Record<string, unknown>;

  // The org currency sits at the document root (contract v1.6); the screen carries it from here on.
  if (isNode(candidate.screen) === false && isScreen(candidate.screen)) {
    const screen = candidate.screen as OverlayScreen;
    return candidate.currency && !screen.currency
      ? { ...screen, currency: candidate.currency }
      : screen;
  }
  if (isScreen(candidate)) return candidate as OverlayScreen;
  if (isNode(candidate)) return { layout: candidate as OverlayNode };
  return null;
}

function isNode(value: unknown): boolean {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as OverlayNode).type === 'string'
  );
}

function isScreen(value: unknown): boolean {
  return (
    !!value &&
    typeof value === 'object' &&
    isNode((value as OverlayScreen).layout)
  );
}
